import {
  Box,
  ImageListItem,
  ImageListItemBar,
  Paper,
  Theme,
} from "@mui/material";

import { useState } from "react";
import { Device } from "../../../../../../API";
import { S3ImageFullScreen } from "../../../../../../common/components/media/S3ImageFullScreen";
import { useS3ObjectLastModified } from "../../../../../../common/hooks/useS3ObjectLastModified";
import { useStream } from "../../../../../../common/hooks/useStream";
import { extractDeviceNameFromId } from "../../../../../../common/utils/getEntityName";
import { selectedStreamObjectVariable } from "../../../../../../common/variables/selectedStreamObject";
import { useGetLocationsCached } from "../../../../../customer-settings/tabs/locations/hooks/useGetLocationsCached";
import { usePublishNode } from "../../../../../devices/hooks/usePublishNode";
import DeviceImageListItemBarTitle from "./DeviceImageListItemBarTitle";

interface IDeviceImageListItemProps {
  device: Device;
}

const DeviceImageListItem = ({
  device,
}: IDeviceImageListItemProps): JSX.Element => {
  const { locations } = useGetLocationsCached("cache-only");

  const [isReady, setIsReady] = useState(true);
  const [open, setOpen] = useState(false);
  const [currentDevice, setCurrentDevice] = useState("");
  const { lastModified: imageDate } = useS3ObjectLastModified(
    device.deviceData.signedUrlRecentFrame
  );

  const deviceLocationId = device.locationId;

  const locationFromQuery = locations.find(l => l.id === deviceLocationId);

  const { getStreamName } = useStream();
  const { publishNode } = usePublishNode();

  const { extractedDeviceName } = extractDeviceNameFromId(device.id);

  const handleLiveViewOnClick = (): void => {
    if (!isReady) return;

    // todo: come up with a better unique naming scheme
    setCurrentDevice(device.id);

    const streamName = getStreamName(device.nodeId, device.id);

    publishNode({
      message: JSON.stringify({
        TARGET: "KINESIS_STREAM",
        ACTION: "KINESIS_STREAM_START",
        streamName,
        deviceName: extractedDeviceName,
      }),
      nodeId: device.nodeId,
    }).then((): void => {
      selectedStreamObjectVariable({
        streamName,
        extractedDeviceName,
        nodeId: device.nodeId,
      });

      setOpen(true);
    });
  };

  return (
    <ImageListItem key={device.id} component="div">
      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: (theme: Theme): string =>
            `1px solid ${theme.palette.divider}`,
          width: "100%",
          height: "100%",
          padding: "0.5em",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "220px",
            borderRadius: "8px",
            border: (theme): string =>
              `1px solid ${theme.palette.otherOutlineBorder.main}`,
          }}
        >
          <S3ImageFullScreen
            sx={{
              width: "100%",
              height: device.deviceData.signedUrlRecentFrame ? "100%" : "220px",
              maxHeight: "220px",
              borderRadius: "8px",
            }}
            s3Key={device.deviceData.signedUrlRecentFrame ?? ""}
            onClickVideoIcon={handleLiveViewOnClick}
          />
        </Box>
        <ImageListItemBar
          title={
            <DeviceImageListItemBarTitle
              deviceName={device.name}
              deviceId={device.id}
              nodeId={device.nodeId}
              locationName={(locationFromQuery?.name as string) ?? "-"}
              deviceData={device.deviceData}
              isReady={isReady}
              setIsReady={setIsReady}
              currentDevice={currentDevice}
              onLiveViewClick={handleLiveViewOnClick}
              open={open}
              setOpen={setOpen}
              imageDate={imageDate}
            />
          }
          position="below"
          sx={{
            padding: "0 0.5em",
          }}
        />
      </Paper>
    </ImageListItem>
  );
};

export default DeviceImageListItem;
